import { useState } from 'react'

import { Field, useFormikContext } from 'formik'
import { useRentalHistoryManagers } from 'hooks/useRentalHistoryQueries'

import { Box, Button } from 'app/components/design-system-components'
import {
  AutocompleteField,
  InputField,
  TextArea,
} from 'app/components/design-system-components/inputs'
import { CheckboxInputField } from 'app/components/design-system-components/inputs/Checkboxes/Checkbox'
import { SelectField } from 'app/components/design-system-components/inputs/Select/Select'
import {
  Heading,
  Text,
} from 'app/components/design-system-components/typography'
import { FieldWrapper } from 'app/forms/FieldWrapper'
import { debounce, STANDARD_TIMEOUT } from 'app/helpers/debounce'
import { formFields } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/form.utils'
import {
  CustomMobileInput,
  StyledFieldWrapper,
  StyledFlexBox,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/styled-form-component'
import type { RentalHistoryFormValues } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import {
  isAddressHistoryOwned,
  isAddressHistoryTemporaryBnBArrangement,
  isAgent,
  isOwner,
} from 'app/sm/helpers'

interface RefereeSectionProps {
  values: RentalHistoryFormValues
  isEditMode: boolean
  isQLDForm22?: boolean
}

// Manager type options
const MANAGER_TYPE_OPTIONS = [
  {
    option: { value: 1, label: 'Agent' },
    optionMeta: {
      id: 1,
      displayText: 'Agent',
      value: 1,
    },
  },
  {
    option: { value: 2, label: 'Owner' },
    optionMeta: { id: 2, displayText: 'Owner', value: 2 },
  },
]

/**
 * Referee section component
 * Handles reference contact information and consent
 */
export const RefereeSection: React.FC<RefereeSectionProps> = ({
  values,
  isEditMode,
  isQLDForm22 = false,
}) => {
  const { setFieldValue } = useFormikContext<RentalHistoryFormValues>()
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [agencySearchText, setAgencySearchText] = useState('')
  const [isAgentSelected, setIsAgentSelected] = useState(false)

  // Use the rental history managers hook for agency search
  const { data: managersData, isLoading: isLoadingManagers } =
    useRentalHistoryManagers(agencySearchText)

  // Convert managers data to options format for AutocompleteField
  const agencyOptions =
    managersData?.map((manager: any) => ({
      option: manager,
      optionMeta: {
        id:
          manager.id ||
          `${manager.firstName}-${manager.lastName}-${manager.agencyName}`,
        displayText: `${manager.firstName} ${manager.lastName} (${manager.agencyName})`,
        value: manager,
      },
    })) || []

  // Handle agency search text change with debouncing
  const handleAgencySearchChange = debounce((searchText: string) => {
    setAgencySearchText(searchText)
  }, STANDARD_TIMEOUT)

  // Handle agent selection from dropdown
  const handleAgentSelection = (selectedOption: any) => {
    if (selectedOption?.option) {
      const agent = selectedOption.option
      setFieldValue('agencyName', agent.agencyName || '')
      setFieldValue('firstName', agent.firstName || '')
      setFieldValue('lastName', agent.lastName || '')
      setFieldValue('email', agent.email || '')
      setFieldValue('phoneNumber', agent.phoneNumber || '')
      setFieldValue('officeNumber', agent.officeNumber || '')
      // Mark that an agent has been selected to show the form fields
      setIsAgentSelected(true)
    }
  }

  const shouldShowReferenceFields =
    !isAddressHistoryOwned(values.propertyArrangement?.optionMeta.value) &&
    !isAddressHistoryTemporaryBnBArrangement(
      values.propertyArrangement?.optionMeta.value,
    )

  const isConsentGiven = values.shouldContactReference
  const isAgentType = isAgent(values.managerType?.optionMeta?.value)
  const isOwnerType = isOwner(values.managerType?.optionMeta?.value)

  if (!shouldShowReferenceFields) {
    return null
  }

  const handleAddManually = () => {
    setShowManualEntry(true)
    setIsAgentSelected(false)
  }

  const handleBackToSearch = () => {
    setShowManualEntry(false)
    // Clear manual form data
    setFieldValue('firstName', '')
    setFieldValue('lastName', '')
    setFieldValue('email', '')
    setFieldValue('agencyName', '')
    setFieldValue('officeNumber', '')
    setFieldValue('phoneNumber', '')
  }

  return (
    <Box my={7}>
      <Heading level={3} displayingLevel={3} mb={4}>
        Referee details
      </Heading>
      <Text mb={7}>Add the applicant’s previous address </Text>

      <StyledFlexBox gap={3} mb={7}>
        <FieldWrapper
          id={formFields.shouldContactReference}
          name={formFields.shouldContactReference}
        >
          <Field name={formFields.shouldContactReference}>
            {({ field }: any) => (
              <CheckboxInputField
                {...field}
                changeHandler={(checked: boolean) => {
                  setFieldValue(formFields.shouldContactReference, checked)
                }}
              >
                The applicant has consented to disclosing their personal details
                for the purpose of rental reference
              </CheckboxInputField>
            )}
          </Field>
        </FieldWrapper>
      </StyledFlexBox>
      <StyledFlexBox gap={3} mb={7}>
        <Box flex={1}>
          {/* Manager Type */}
          <StyledFieldWrapper
            id={formFields.managerType}
            name={formFields.managerType}
            label="Reference Type"
            required
          >
            <Field name={formFields.managerType}>
              {({ field }: any) => (
                <SelectField
                  {...field}
                  options={MANAGER_TYPE_OPTIONS}
                  label="Select reference type"
                />
              )}
            </Field>
          </StyledFieldWrapper>
        </Box>
        <Box flex={1}></Box>
      </StyledFlexBox>

      {/* Agent Search Box - Only show for Agent type when not in manual mode */}
      {isAgentType && !showManualEntry && !isEditMode && (
        <StyledFlexBox gap={3} mb={7}>
          <Box flex={1}>
            <StyledFieldWrapper
              id={formFields.agentSearch}
              name={formFields.agentSearch}
              label="Search for Agency"
            >
              <AutocompleteField
                name={formFields.agentSearch}
                placeholder="Search something"
                onChangeText={handleAgencySearchChange}
                changeHandler={handleAgentSelection}
                isLoading={!!agencySearchText && isLoadingManagers}
                options={agencyOptions}
                disabled={!isConsentGiven}
              />
            </StyledFieldWrapper>
            <Text fontSize="sm" color="muted" mt={2} mb={0}>
              Can't find the agent?{' '}
              <Button
                variant="linkBlueUnderlined"
                size="sm"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault()
                  handleAddManually()
                }}
                p={0}
                h="auto"
                fontWeight="normal"
                textDecoration="underline"
              >
                Add manually
              </Button>
            </Text>
          </Box>
          <Box flex={1}></Box>
        </StyledFlexBox>
      )}

      {/* Manual Form - Show for Owner types, or for Agent types when in manual mode OR when agent is selected */}
      {((isAgentType && (showManualEntry || isAgentSelected)) ||
        isOwnerType ||
        isEditMode) && (
        <Box>
          {/* Back to search button - Only show for Agent in manual mode */}
          {isAgentType && showManualEntry && !isEditMode && (
            <Text fontSize="sm" color="muted" mb={3}>
              <Button
                variant="linkBlueUnderlined"
                size="sm"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault()
                  handleBackToSearch()
                }}
                p={0}
                h="auto"
                fontWeight="normal"
                textDecoration="underline"
              >
                Search for an agent
              </Button>
            </Text>
          )}

          {/* Name Fields - Always show for Owner, show for Agent when in manual entry mode OR when agent is selected */}
          {(isOwnerType ||
            (isAgentType && (showManualEntry || isAgentSelected)) ||
            isEditMode) && (
            <StyledFlexBox gap={3} mb={7}>
              <Box flex={1}>
                <StyledFieldWrapper
                  id={formFields.firstName}
                  name={formFields.firstName}
                  label="First Name"
                  required
                >
                  <Field name={formFields.firstName}>
                    {({ field }: any) => <InputField {...field} />}
                  </Field>
                </StyledFieldWrapper>
              </Box>
              <Box flex={1} ml={6}>
                <StyledFieldWrapper
                  id={formFields.lastName}
                  name={formFields.lastName}
                  label="Last Name"
                  required
                >
                  <Field name={formFields.lastName}>
                    {({ field }: any) => <InputField {...field} />}
                  </Field>
                </StyledFieldWrapper>
              </Box>
            </StyledFlexBox>
          )}

          {/* Email and Agency Name */}
          {(isOwnerType ||
            (isAgentType && (showManualEntry || isAgentSelected)) ||
            isEditMode) && (
            <StyledFlexBox gap={3} mb={7}>
              <Box flex={1}>
                <StyledFieldWrapper
                  id={formFields.email}
                  name={formFields.email}
                  label="Email"
                  required
                >
                  <Field name={formFields.email}>
                    {({ field }: any) => <InputField {...field} type="email" />}
                  </Field>
                </StyledFieldWrapper>
              </Box>
              <Box flex={1}>
                {/* Agency Name - Only show for Agent type in manual entry mode */}
                {isAgentType && (
                  <StyledFieldWrapper
                    id={formFields.agencyName}
                    name={formFields.agencyName}
                    label="Agency Name"
                    required
                  >
                    <Field name={formFields.agencyName}>
                      {({ field }: any) => <InputField {...field} />}
                    </Field>
                  </StyledFieldWrapper>
                )}
              </Box>
            </StyledFlexBox>
          )}

          {/* Phone Numbers */}
          <StyledFlexBox gap={3} mb={7}>
            <Box flex={1}>
              <StyledFieldWrapper
                id={formFields.officeNumber}
                name={formFields.officeNumber}
                label="Office Number"
                required
              >
                <CustomMobileInput
                  name={formFields.officeNumber}
                  placeholder={`+61 412 345 678`}
                />
              </StyledFieldWrapper>
            </Box>
            <Box flex={1}>
              <StyledFieldWrapper
                id={formFields.phoneNumber}
                name={formFields.phoneNumber}
                label="Mobile Number"
                required
              >
                <CustomMobileInput
                  name={formFields.phoneNumber}
                  placeholder={`+61 412 345 678`}
                />
              </StyledFieldWrapper>
            </Box>
          </StyledFlexBox>
        </Box>
      )}

      {/* Message to Referee - Optional - Show when not QLD Form 22 and referee details should be shown */}
      {!isQLDForm22 && (
        <StyledFlexBox gap={3} mb={7}>
          <Box flex={1}>
            <FieldWrapper
              id={formFields.personalMessage}
              name={formFields.personalMessage}
              label="Message to Referee (Optional)"
              // @ts-expect-error: labelProps may not be typed, but is supported by FieldWrapper
              labelProps={{
                subText: (
                  <Text mb={0} mt={2}>
                    This message is included in the email rental reference
                    request to your Referee only. It's not displayed on your
                    application to a prospective property manager.
                  </Text>
                ),
              }}
            >
              <Field
                name={formFields.personalMessage}
                as={TextArea}
                rows={4}
                placeholder="Including a message to your Referee is optional."
              />
            </FieldWrapper>
          </Box>
        </StyledFlexBox>
      )}
    </Box>
  )
}
